import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Box, Button, Container } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAssignmentDetailV4,
  fetchAllAssignmentUserProgressV4,
  fetchAssignmentCredits,
  fetchLinkedServices,
  fetchGroups,
  fetchLightweightUsersV4,
  selectActiveAssignmentDetailV4,
  selectActiveAssignmentDetailV4Status,
  selectAssignmentCredits,
  selectAssignmentUserProgressV4,
  selectLinkedServicesStatus,
  selectGroupsStatus,
  selectLightweightUsersV4Status,
  fetchAssignmentSummariesV4,
} from 'src/v3/manage/manage.slice'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { InfoIcon } from 'src/v3/core/icons'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import EditDeleteButtons from '../components/edit-delete-buttons'
import ManualCreditsInfoDialog from '../components/manual-credits-info-dialog'
import AssignmentDetailsCard from './components/assignment-details-card'
import ProgressCardTabbed from './components/progress-card-tabbed'
import BulkAssignmentCreditModal from './components/modals/bulk-assignment-credit-modal'
import { deleteAssignment, giveCourseCredit } from '../utils/api'
import { useNotifications } from '../hooks/use-notifications'
import DeleteConfirmationModal from '../components/modals/delete-confirmation-modal'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { ASSIGNMENTS_URLS } from '../constants'
import CoursesProgressCard from './components/courses-progress-card'
import UserProgressCard from './components/user-progress-card'

export default function AssignmentDetailV4(): ReactElement {
  useScrollToTop()

  let { assignmentID } = useParams<{ assignmentID: string }>()
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let location = useLocation()
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  let [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  let [creditInfoOpen, setCreditInfoOpen] = useState(false)
  let [isBulkCreditModalOpen, setIsBulkCreditModalOpen] =
    useState<boolean>(false)
  let [selectedUserIDsForBulkCredit, setSelectedUserIDsForBulkCredit] =
    useState<string[]>([])

  let assignment = useAppSelector(selectActiveAssignmentDetailV4)
  let userProgress = useAppSelector(selectAssignmentUserProgressV4)
  let credits = useAppSelector(selectAssignmentCredits)
  let assignmentDetailStatus = useAppSelector(
    selectActiveAssignmentDetailV4Status,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let userGroupsStatus = useAppSelector(selectGroupsStatus)
  let usersStatus = useAppSelector(selectLightweightUsersV4Status)
  let linkedServiceName = useLinkedServiceName(assignment?.linkedServiceID)
  let isLoading =
    assignmentDetailStatus === 'pending' || linkedServicesStatus === 'pending'

  let handleEdit = useCallback(() => {
    if (!assignment) return
    navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignment.id}/edit`)
  }, [assignment])

  let handleDelete = useCallback(async () => {
    if (!assignment) return

    setIsSubmitting(true)
    await deleteAssignment(assignment.id)
      .then(() => {
        dispatch(fetchAssignmentSummariesV4())
        setIsDeleteModalOpen(false)
        navigate(ASSIGNMENTS_URLS.INDEX, {
          state: {
            linkedServiceID: assignment.linkedServiceID,
            successMessage: `Assignment "${assignment.name}" deleted successfully.`,
          },
        })
      })
      .catch((error) => {
        let errorMessage = `Error deleting assignment "${assignment.name}".`
        // eslint-disable-next-line no-console
        console.error(
          errorMessage,
          'Assignment ID: ',
          assignmentID,
          'Error: ',
          error,
        )
        showError(errorMessage)
      })
      .finally(() => setIsSubmitting(false))
  }, [assignment, dispatch, showError, assignmentID, navigate])

  let handleOpenBulkCreditModal = useCallback((userIDs: string[]) => {
    setSelectedUserIDsForBulkCredit(userIDs)
    setIsBulkCreditModalOpen(true)
  }, [])

  let handleCloseBulkCreditModal = useCallback(() => {
    setIsBulkCreditModalOpen(false)
    setSelectedUserIDsForBulkCredit([])
  }, [])

  let handleBulkCreditSubmit = useCallback(async () => {
    if (!assignmentID || selectedUserIDsForBulkCredit.length === 0) return

    setIsSubmitting(true)

    let payload = {
      assignmentID,
      userIDs: selectedUserIDsForBulkCredit,
      courseIDs: assignment?.courses?.map((c) => c.id) || [],
    }

    await giveCourseCredit(payload)
      .then(() => {
        dispatch(fetchAssignmentDetailV4(assignmentID))
        dispatch(
          fetchAllAssignmentUserProgressV4({
            assignmentId: assignmentID,
            includeDetails: true,
          }),
        )
        dispatch(fetchAssignmentCredits(assignmentID))
      })
      .then(() => {
        handleCloseBulkCreditModal()
        showSuccess(
          `Successfully gave credit for assignment "${assignment?.name}" to selected users.`,
        )
      })
      .catch((err) => {
        let errorMessage = `Error giving credit for assignment "${assignment?.name}" to selected users.`
        // eslint-disable-next-line no-console
        console.error(errorMessage, 'Payload: ', payload, 'Error: ', err)
        showError(errorMessage)
      })
      .finally(() => setIsSubmitting(false))
  }, [
    assignmentID,
    selectedUserIDsForBulkCredit,
    assignment,
    dispatch,
    handleCloseBulkCreditModal,
  ])

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  useEffect(() => {
    if (
      assignmentDetailStatus === 'uninitialized' ||
      assignment?.id !== assignmentID
    ) {
      dispatch(fetchAssignmentDetailV4(assignmentID!))
    }
  }, [assignmentID, assignmentDetailStatus, assignment?.id, dispatch])

  useEffect(() => {
    if (assignmentID) {
      dispatch(
        fetchAllAssignmentUserProgressV4({
          assignmentId: assignmentID,
          includeDetails: true,
        }),
      )
      dispatch(fetchAssignmentCredits(assignmentID))
    }
  }, [assignmentID, dispatch])

  useAutoFetch(userGroupsStatus, fetchGroups)

  useEffect(() => {
    if (
      assignment?.linkedServiceID &&
      (!usersStatus[assignment?.linkedServiceID] ||
        usersStatus[assignment?.linkedServiceID] === 'uninitialized')
    ) {
      dispatch(fetchLightweightUsersV4(assignment?.linkedServiceID))
    }
  }, [userGroupsStatus, assignment?.linkedServiceID])

  useEffect(() => {
    let state = location.state as { successMessage?: string } | null
    if (state?.successMessage) {
      showSuccess(state.successMessage)
      // Clear the state to prevent message showing on refresh
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location, navigate, showSuccess])

  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <PageTitleWithLinkedService
          pageTitle={assignment?.name ?? ''}
          linkedServiceName={linkedServiceName}
          moduleName="Assignments"
          handleBackClick={() =>
            navigate(ASSIGNMENTS_URLS.INDEX, {
              state: { linkedServiceID: assignment?.linkedServiceID },
            })
          }
          isLoading={assignmentDetailStatus === 'pending'}
        />

        <EditDeleteButtons
          moduleName="Assignment"
          canSubmit={true}
          isSubmitting={false}
          isDisabled={isLoading || isSubmitting}
          handleEdit={handleEdit}
          handleDelete={() => setIsDeleteModalOpen(true)}
        />
      </Box>
      <AssignmentDetailsCard assignment={assignment} isLoading={isLoading} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button
          size="small"
          startIcon={<InfoIcon sx={{ fontSize: 16 }} />}
          onClick={() => setCreditInfoOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          About manual credits
        </Button>
      </Box>
      <CoursesProgressCard
        assignment={assignment}
        isLoading={isLoading}
        onViewCourseProgress={(courseID) =>
          navigate(
            `${ASSIGNMENTS_URLS.INDEX}/${assignmentID}/courses/${courseID}/progress`,
          )
        }
      />
      <UserProgressCard
        assignment={assignment}
        isSubmitting={isSubmitting}
        isLoading={isLoading}
        credits={credits}
        onViewAssignmentProgress={(userID) =>
          navigate(
            `${ASSIGNMENTS_URLS.INDEX}/${assignmentID}/users/${userID}/progress`,
          )
        }
        onGiveBulkCredit={handleOpenBulkCreditModal}
      />
      <ProgressCardTabbed
        assignment={assignment}
        isLoading={isLoading}
        credits={credits}
      />
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        entityName="assignment"
        entityDisplayName={assignment?.name || ''}
        isSubmitting={isSubmitting}
      />
      <BulkAssignmentCreditModal
        isOpen={isBulkCreditModalOpen}
        selectedUserIDs={selectedUserIDsForBulkCredit}
        progressByUsers={userProgress}
        isSubmitting={isSubmitting}
        handleClose={handleCloseBulkCreditModal}
        handleSubmit={handleBulkCreditSubmit}
      />
      <ManualCreditsInfoDialog
        open={creditInfoOpen}
        onClose={() => setCreditInfoOpen(false)}
      />
      {NotificationComponent}
    </Container>
  )
}
